import type { AppLanguage } from "~/i18n/languagePreference";

import { localeTagFor } from "~/helpers/activeLocale";
import { narrowCurrencySymbolFor } from "~/helpers/localeNumberFormat";
import { relativeTimestampParts } from "~/helpers/timestampFormat";
import { normalizeLanguage } from "~/i18n/languagePreference";

export type DateInput = Date | string | number;

export interface RelativeTimestampLabels {
	justNow: string;
}

export interface LocaleFormatters {
	language: AppLanguage;
	locale: string;
	formatDate: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string;
	formatTime: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string;
	formatDateTime: (value: DateInput, options?: Intl.DateTimeFormatOptions) => string;
	formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
	formatCurrency: (value: number, currency?: string, options?: Intl.NumberFormatOptions) => string;
	formatPercent: (value: number, options?: Intl.NumberFormatOptions) => string;
	formatRelativeTimestamp: (timestamp: string, labels: RelativeTimestampLabels, now?: Date) => string;
}

const toDate = (value: DateInput): Date => (value instanceof Date ? value : new Date(value));

export const utcDateOnlyFormat: Intl.DateTimeFormatOptions = {
	day: "numeric",
	month: "short",
	timeZone: "UTC",
	year: "numeric"
};

const relativeFallbackDateFormat: Intl.DateTimeFormatOptions = {
	day: "numeric",
	month: "short",
	year: "numeric"
};

export const createLocaleFormatters = (language: AppLanguage): LocaleFormatters => {
	const resolved = normalizeLanguage(language);
	const locale = localeTagFor(resolved);

	const formatDate = (value: DateInput, options: Intl.DateTimeFormatOptions = { dateStyle: "medium" }): string =>
		new Intl.DateTimeFormat(locale, options).format(toDate(value));

	const formatTime = (value: DateInput, options: Intl.DateTimeFormatOptions = { timeStyle: "short" }): string =>
		new Intl.DateTimeFormat(locale, options).format(toDate(value));

	const formatDateTime = (
		value: DateInput,
		options: Intl.DateTimeFormatOptions = { dateStyle: "medium", timeStyle: "short" }
	): string => new Intl.DateTimeFormat(locale, options).format(toDate(value));

	const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string =>
		new Intl.NumberFormat(locale, options).format(value);

	const formatCurrency = (value: number, currency = "USD", options?: Intl.NumberFormatOptions): string =>
		new Intl.NumberFormat(locale, {
			...narrowCurrencySymbolFor(currency),
			currency,
			style: "currency",
			...options
		}).format(value);

	const formatPercent = (value: number, options?: Intl.NumberFormatOptions): string =>
		new Intl.NumberFormat(locale, { style: "percent", ...options }).format(value);

	const relativeTimeFormat = new Intl.RelativeTimeFormat(locale, { numeric: "always", style: "narrow" });

	const formatRelativeTimestamp = (timestamp: string, labels: RelativeTimestampLabels, now?: Date): string => {
		const parts = relativeTimestampParts(timestamp, now);
		switch (parts.kind) {
			case "blank":
				return "";
			case "justNow":
				return labels.justNow;
			case "relative":
				return relativeTimeFormat.format(-parts.count, parts.unit);
			case "date":
				return formatDate(parts.date, relativeFallbackDateFormat);
		}
	};

	return {
		language: resolved,
		locale,
		formatDate,
		formatTime,
		formatDateTime,
		formatNumber,
		formatCurrency,
		formatPercent,
		formatRelativeTimestamp
	};
};
